import { useEffect, useMemo, useRef, useState } from 'react';
import { Stage, Layer, Image as KonvaImage, Rect, Ellipse, Line, Text, Arrow } from 'react-konva';
import type Konva from 'konva';
import {
  Square,
  Circle,
  MoveUpRight,
  Minus,
  Type as TypeIcon,
  Ruler,
  Trash2,
  Loader2,
  EyeOff,
  Eye,
  Lock,
  Sparkles,
  Check,
} from 'lucide-react';

/**
 * Zeichnen auf einem Foto.
 *
 * Drei Dinge, die den Entwurf bestimmen:
 *
 * 1. Das Bild wird nie verändert. Die Markierungen liegen als eigene
 *    Ebene darüber und stehen als Datensätze in der Datenbank. Ein
 *    Prüffoto ist ein Beweismittel -- wer später bestreitet, dass ein
 *    Mangel bestand, muss sich an der Aufnahme messen lassen.
 *
 * 2. Koordinaten sind Anteile der Bildbreite bzw. -höhe, keine Pixel.
 *    Dieselbe Markierung sitzt damit auf der Miniatur, in der
 *    Vollansicht und im PDF an derselben Stelle -- unabhängig davon, in
 *    welcher Größe sie gesetzt wurde.
 *
 * 3. Ein Maß entsteht nur mit gesetztem Maßstab. Vorher ist eine Strecke
 *    im Bild eine Anzahl Pixel und keine Länge.
 */

export type Form =
  | 'RECHTECK'
  | 'ELLIPSE'
  | 'PFEIL'
  | 'LINIE'
  | 'FREIHAND'
  | 'TEXT'
  | 'MASS'
  | 'SCHWAERZUNG';

export type Stufe = 'KUNDE' | 'INTERN' | 'VERTRAULICH';

export interface Annotation {
  id: number;
  form: Form;
  geometrie: any;
  text: string | null;
  farbe: string;
  strichstaerke: number;
  nummer: number | null;
  sichtbarkeit: Stufe;
  mass_mm: number | null;
  merkmal: string | null;
  bewertung: 'INNERHALB' | 'UEBERSCHRITTEN' | 'UNTERSCHRITTEN' | null;
  herkunft: 'MENSCH' | 'KI_VORSCHLAG' | 'KI_BESTAETIGT';
  ki_zuversicht: number | null;
}

interface Werkzeug {
  form: Form;
  bezeichnung: string;
  symbol: typeof Square;
  /** Wie viele Punkte setzt der Benutzer? */
  punkte: 2 | 'zug';
}

const WERKZEUGE: Werkzeug[] = [
  { form: 'RECHTECK', bezeichnung: 'Rechteck', symbol: Square, punkte: 2 },
  { form: 'ELLIPSE', bezeichnung: 'Ellipse', symbol: Circle, punkte: 2 },
  { form: 'PFEIL', bezeichnung: 'Pfeil', symbol: MoveUpRight, punkte: 2 },
  { form: 'LINIE', bezeichnung: 'Linie', symbol: Minus, punkte: 2 },
  { form: 'TEXT', bezeichnung: 'Beschriftung', symbol: TypeIcon, punkte: 2 },
  { form: 'MASS', bezeichnung: 'Messen', symbol: Ruler, punkte: 2 },
  { form: 'SCHWAERZUNG', bezeichnung: 'Schwärzen', symbol: EyeOff, punkte: 2 },
];

/**
 * Die drei Stufen -- dieselben wie am Foto und an einer Notiz.
 *
 * Vorgabe ist KUNDE: eine Markierung wird gesetzt, damit sie jemand
 * sieht. Die umgekehrte Vorgabe führte dazu, dass im Bericht Bilder ohne
 * die Pfeile stehen, die den Befund erst zeigen.
 */
const STUFEN: Array<{ wert: Stufe; bezeichnung: string; symbol: typeof Eye }> = [
  { wert: 'KUNDE', bezeichnung: 'Kunde', symbol: Eye },
  { wert: 'INTERN', bezeichnung: 'Intern', symbol: EyeOff },
  { wert: 'VERTRAULICH', bezeichnung: 'Vertraulich', symbol: Lock },
];

const FARBEN = ['#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#2563eb', '#000000'];

interface Props {
  bildUrl: string;
  annotationen: Annotation[];
  /** px pro mm; ohne Maßstab ist Messen gesperrt. */
  massstabPxProMm: number | null;
  /** Breite des Originals in Pixeln -- nötig, um Pixellängen zu melden. */
  bildBreite: number;
  darfBearbeiten: boolean;
  onAnlegen: (a: {
    form: Form;
    geometrie: any;
    text?: string;
    farbe: string;
    strichstaerke: number;
    sichtbarkeit: Stufe;
    merkmal?: string;
    laenge_px?: number;
  }) => Promise<void>;
  onLoeschen: (id: number) => Promise<void>;
  onMassstabSetzen?: (laengePx: number) => void;
  /** Stufe einer vorhandenen Markierung ändern. */
  onSichtbarkeit?: (id: number, stufe: Stufe) => Promise<void>;
  /** Aus einem KI-Vorschlag eine Aussage machen. */
  onBestaetigen?: (id: number) => Promise<void>;
  /** Die KI Markierungen vorschlagen lassen. */
  onKiVorschlagen?: () => Promise<void>;
}

export default function FotoAnnotator({
  bildUrl,
  annotationen,
  massstabPxProMm,
  bildBreite,
  darfBearbeiten,
  onAnlegen,
  onLoeschen,
  onMassstabSetzen,
  onSichtbarkeit,
  onBestaetigen,
  onKiVorschlagen,
}: Props) {
  const huelle = useRef<HTMLDivElement>(null);
  const [bild, setBild] = useState<HTMLImageElement | null>(null);
  const [flaeche, setFlaeche] = useState({ breite: 0, hoehe: 0 });
  const [werkzeug, setWerkzeug] = useState<Form>('RECHTECK');
  const [farbe, setFarbe] = useState(FARBEN[0]);
  const [staerke, setStaerke] = useState(3);
  const [stufe, setStufe] = useState<Stufe>('KUNDE');
  const [entwurf, setEntwurf] = useState<{ von: number[]; nach: number[] } | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<number | null>(null);

  // ---- Bild laden ----
  useEffect(() => {
    const b = new window.Image();
    b.crossOrigin = 'anonymous';
    b.src = bildUrl;
    b.onload = () => setBild(b);
    return () => {
      b.onload = null;
    };
  }, [bildUrl]);

  // ---- Anzeigefläche an die Hülle anpassen ----
  useEffect(() => {
    if (!bild || !huelle.current) return;
    const anpassen = (): void => {
      const breite = huelle.current!.clientWidth;
      const hoehe = (breite * bild.height) / bild.width;
      setFlaeche({ breite, hoehe });
    };
    anpassen();
    // Das Fenster kann sich drehen -- vor Ort auf dem Handy der Normalfall.
    window.addEventListener('resize', anpassen);
    return () => window.removeEventListener('resize', anpassen);
  }, [bild]);

  /** Bildschirmkoordinate zu Anteil (0 bis 1). */
  function alsAnteil(p: { x: number; y: number }): number[] {
    return [
      Math.min(Math.max(p.x / flaeche.breite, 0), 1),
      Math.min(Math.max(p.y / flaeche.hoehe, 0), 1),
    ];
  }

  /** Anteil zurück auf die Anzeigefläche. */
  const zuPixel = useMemo(
    () => (a: number[]) => ({ x: a[0] * flaeche.breite, y: a[1] * flaeche.hoehe }),
    [flaeche]
  );

  /**
   * Länge einer Strecke in Pixeln des ORIGINALS.
   *
   * Nicht in Bildschirmpixeln: die hängen davon ab, wie breit das Fenster
   * gerade ist -- und ein Maß, das sich beim Drehen des Handys ändert,
   * wäre wertlos.
   */
  function laengeImOriginal(von: number[], nach: number[]): number {
    const dx = (nach[0] - von[0]) * bildBreite;
    const dy = ((nach[1] - von[1]) * bildBreite * flaeche.hoehe) / flaeche.breite;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function beginn(e: Konva.KonvaEventObject<MouseEvent | TouchEvent>): void {
    if (!darfBearbeiten || laeuft) return;
    const p = e.target.getStage()?.getPointerPosition();
    if (!p) return;
    const a = alsAnteil(p);
    setEntwurf({ von: a, nach: a });
    setGewaehlt(null);
  }

  function bewegung(e: Konva.KonvaEventObject<MouseEvent | TouchEvent>): void {
    if (!entwurf) return;
    const p = e.target.getStage()?.getPointerPosition();
    if (!p) return;
    setEntwurf({ ...entwurf, nach: alsAnteil(p) });
  }

  async function ende(): Promise<void> {
    if (!entwurf) return;
    const { von, nach } = entwurf;
    setEntwurf(null);

    // Ein Klick ohne Zug ist keine Markierung, sondern ein Fehlgriff.
    const winzig =
      Math.abs(nach[0] - von[0]) < 0.01 && Math.abs(nach[1] - von[1]) < 0.01;
    if (winzig) return;

    const laengePx = laengeImOriginal(von, nach);

    if (werkzeug === 'MASS' && !massstabPxProMm) {
      onMassstabSetzen?.(laengePx);
      return;
    }

    let text: string | undefined;
    if (werkzeug === 'TEXT') {
      const eingabe = window.prompt('Beschriftung');
      if (!eingabe) return;
      text = eingabe;
    }

    /*
     * Beim Messen wird nach dem Merkmal gefragt und nicht danach, was
     * herauskommt. Ohne Merkmal ist der Millimeterwert eine Zahl ohne
     * Bezug -- und ohne Bezug gibt es auch keinen Grenzwert, gegen den
     * sie gehalten werden könnte.
     */
    let merkmal: string | undefined;
    if (werkzeug === 'MASS') {
      const eingabe = window.prompt(
        'Woran wird gemessen? (etwa „Falzluft“ oder „Spaltmaß Wandanschluss“)\n\n' +
          'Die Angabe entscheidet, gegen welchen Grenzwert der Wert gehalten wird. ' +
          'Ohne sie bleibt er unbewertet.',
        'Falzluft'
      );
      merkmal = eingabe?.trim() || undefined;
    }

    const geometrie =
      werkzeug === 'RECHTECK' ||
      werkzeug === 'ELLIPSE' ||
      werkzeug === 'TEXT' ||
      werkzeug === 'SCHWAERZUNG'
        ? {
            x: Math.min(von[0], nach[0]),
            y: Math.min(von[1], nach[1]),
            breite: Math.abs(nach[0] - von[0]),
            hoehe: Math.abs(nach[1] - von[1]),
          }
        : { von, nach };

    setLaeuft(true);
    try {
      await onAnlegen({
        form: werkzeug,
        geometrie,
        text,
        farbe,
        strichstaerke: staerke,
        // Eine Schwärzung ist keine Anmerkung, sondern eine Anweisung an
        // die Auslieferung. Sie greift in jeder Fassung, die das Haus
        // verlässt -- eine Stufe daran wäre eine Zusicherung, die nicht
        // hält.
        sichtbarkeit: werkzeug === 'SCHWAERZUNG' ? 'KUNDE' : stufe,
        ...(merkmal ? { merkmal } : {}),
        ...(werkzeug === 'MASS' ? { laenge_px: laengePx } : {}),
      });
    } finally {
      setLaeuft(false);
    }
  }

  if (!bild) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-sm text-gray-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Bild wird geladen…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {darfBearbeiten && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white p-2">
          {WERKZEUGE.map((w) => {
            const gesperrt = w.form === 'MASS' && !massstabPxProMm;
            const Symbol = w.symbol;
            return (
              <button
                key={w.form}
                type="button"
                onClick={() => setWerkzeug(w.form)}
                title={
                  gesperrt
                    ? 'Erst den Maßstab setzen: ziehen Sie eine Strecke, deren wahre Länge Sie kennen.'
                    : w.bezeichnung
                }
                className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs ${
                  werkzeug === w.form
                    ? 'border-primary-600 bg-primary-50 text-primary-800'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Symbol className="h-3.5 w-3.5" />
                {w.bezeichnung}
                {gesperrt && <span className="text-[10px] text-amber-600">(Maßstab fehlt)</span>}
              </button>
            );
          })}

          <span className="mx-1 h-5 w-px bg-gray-200" />

          {FARBEN.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFarbe(f)}
              title={f}
              style={{ backgroundColor: f }}
              className={`h-6 w-6 rounded-full border-2 ${
                farbe === f ? 'border-gray-900' : 'border-white'
              }`}
            />
          ))}

          <span className="mx-1 h-5 w-px bg-gray-200" />

          <div className="flex items-center gap-1">
            {STUFEN.map((st) => {
              const Symbol = st.symbol;
              return (
                <button
                  key={st.wert}
                  type="button"
                  onClick={() => setStufe(st.wert)}
                  title={
                    st.wert === 'KUNDE'
                      ? 'Erscheint im Kundendokument'
                      : 'Bleibt im Haus — erscheint in keinem Kundendokument'
                  }
                  className={`flex items-center gap-1 rounded-md border px-2 py-1.5 text-xs ${
                    stufe === st.wert
                      ? 'border-primary-600 bg-primary-50 text-primary-800'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <Symbol className="h-3.5 w-3.5" />
                  {st.bezeichnung}
                </button>
              );
            })}
          </div>

          <span className="mx-1 h-5 w-px bg-gray-200" />

          {onKiVorschlagen && (
            <button
              type="button"
              onClick={() => void onKiVorschlagen()}
              title="Die KI schlägt Markierungen vor. Jeder Vorschlag muss bestätigt werden und erscheint bis dahin in keinem Dokument."
              className="flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900 hover:bg-amber-100"
            >
              <Sparkles className="h-3.5 w-3.5" />
              KI-Vorschläge
            </button>
          )}

          <label className="flex items-center gap-1.5 text-xs text-gray-600">
            Stärke
            <input
              type="range"
              min={1}
              max={10}
              value={staerke}
              onChange={(e) => setStaerke(Number(e.target.value))}
              className="w-20"
            />
          </label>
        </div>
      )}

      <div
        ref={huelle}
        className="overflow-hidden rounded-lg border border-gray-200 bg-gray-900"
      >
        {flaeche.breite > 0 && (
          <Stage
            width={flaeche.breite}
            height={flaeche.hoehe}
            onMouseDown={beginn}
            onMouseMove={bewegung}
            onMouseUp={ende}
            onTouchStart={beginn}
            onTouchMove={bewegung}
            onTouchEnd={ende}
            style={{ cursor: darfBearbeiten ? 'crosshair' : 'default' }}
          >
            <Layer listening={false}>
              <KonvaImage image={bild} width={flaeche.breite} height={flaeche.hoehe} />
            </Layer>

            <Layer>
              {annotationen.map((a) => (
                <Markierung
                  key={a.id}
                  a={a}
                  zuPixel={zuPixel}
                  flaeche={flaeche}
                  hervorgehoben={gewaehlt === a.id}
                  onKlick={() => setGewaehlt(a.id === gewaehlt ? null : a.id)}
                />
              ))}

              {entwurf && (
                <Entwurf form={werkzeug} entwurf={entwurf} farbe={farbe} staerke={staerke} zuPixel={zuPixel} />
              )}
            </Layer>
          </Stage>
        )}
      </div>

      {annotationen.length > 0 && (
        <ul className="space-y-1 text-sm">
          {annotationen.map((a) => (
            <li
              key={a.id}
              className={`flex items-center gap-2 rounded px-2 py-1 ${
                gewaehlt === a.id ? 'bg-primary-50' : ''
              }`}
              onMouseEnter={() => setGewaehlt(a.id)}
              onMouseLeave={() => setGewaehlt(null)}
            >
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white"
                style={{ backgroundColor: a.farbe }}
              >
                {a.nummer}
              </span>
              <span className="min-w-0 flex-1 truncate text-gray-800">
                {a.text || WERKZEUGE.find((w) => w.form === a.form)?.bezeichnung}
                {a.merkmal && <span className="ml-2 text-gray-500">{a.merkmal}</span>}
                {a.mass_mm != null && (
                  <strong className="ml-2 font-mono">{a.mass_mm} mm</strong>
                )}
                {/*
                  Die Überschreitung wird benannt und nicht nur eingefärbt:
                  eine Farbe allein ist auf einem Schwarzweißausdruck keine
                  Aussage mehr.
                */}
                {a.bewertung === 'UEBERSCHRITTEN' && (
                  <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800">
                    Grenzwert überschritten
                  </span>
                )}
                {a.bewertung === 'UNTERSCHRITTEN' && (
                  <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800">
                    Grenzwert unterschritten
                  </span>
                )}
                {a.bewertung === 'INNERHALB' && (
                  <span className="ml-2 rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">
                    innerhalb
                  </span>
                )}
                {a.mass_mm != null && a.bewertung == null && (
                  <span
                    title="Für dieses Merkmal ist kein Grenzwert hinterlegt. Der Wert bleibt unbewertet — gegen einen geratenen Grenzwert zu prüfen wäre schlechter als gar nicht zu prüfen."
                    className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600"
                  >
                    unbewertet
                  </span>
                )}
              </span>

              {a.form === 'SCHWAERZUNG' ? (
                <span
                  title="Wird in der Kundenfassung in die Bilddaten gebrannt — unabhängig von jeder Stufe."
                  className="shrink-0 rounded bg-gray-900 px-1.5 py-0.5 text-xs text-white"
                >
                  Schwärzung
                </span>
              ) : (
                darfBearbeiten &&
                onSichtbarkeit && (
                  <select
                    value={a.sichtbarkeit}
                    onChange={(e) => void onSichtbarkeit(a.id, e.target.value as Stufe)}
                    title="Was davon im Kundendokument erscheint"
                    className="shrink-0 rounded border border-gray-300 px-1 py-0.5 text-xs text-gray-700"
                  >
                    {STUFEN.map((st) => (
                      <option key={st.wert} value={st.wert}>
                        {st.bezeichnung}
                      </option>
                    ))}
                  </select>
                )
              )}

              {a.herkunft === 'KI_VORSCHLAG' && (
                <span
                  title="Vorschlag der KI. Er erscheint in keinem Dokument, solange ihn niemand bestätigt hat."
                  className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800"
                >
                  KI-Vorschlag, nicht bestätigt
                  {a.ki_zuversicht != null && ` (${Math.round(a.ki_zuversicht * 100)} %)`}
                </span>
              )}
              {a.herkunft === 'KI_VORSCHLAG' && darfBearbeiten && onBestaetigen && (
                <button
                  type="button"
                  onClick={() => void onBestaetigen(a.id)}
                  title="Bestätigen — damit steht Ihr Name dafür ein, und die Markierung erscheint im Dokument."
                  className="shrink-0 rounded p-1 text-green-600 hover:bg-green-50"
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
              )}
              {a.herkunft === 'KI_BESTAETIGT' && (
                <span className="shrink-0 rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">
                  KI, bestätigt
                </span>
              )}
              {darfBearbeiten && (
                <button
                  type="button"
                  onClick={() => void onLoeschen(a.id)}
                  title="Markierung entfernen — das Foto bleibt unverändert"
                  className="shrink-0 rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {darfBearbeiten && werkzeug === 'SCHWAERZUNG' && (
        <p className="rounded-md border border-gray-300 bg-gray-50 p-3 text-xs text-gray-700">
          <strong>Schwärzen.</strong> Der markierte Bereich wird beim Erzeugen der
          Kundenfassung <em>in den Bilddaten</em> überschrieben — nicht nur
          überdeckt. Das Original bleibt unverändert erhalten; hier auf dem
          Bildschirm sehen Sie es weiterhin, im Kundendokument nicht mehr.
        </p>
      )}

      {!massstabPxProMm && darfBearbeiten && (
        <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
          <strong>Kein Maßstab gesetzt.</strong> Wählen Sie „Messen“ und ziehen Sie
          eine Strecke, deren wahre Länge Sie kennen — einen Zollstock, ein
          Normmaß. Ohne bekannte Bezugsgröße ist ein Abstand im Bild keine Länge:
          eine Tür ist auf dem Foto so breit, wie der Fotograf stand.
        </p>
      )}
    </div>
  );
}

function Markierung({
  a,
  zuPixel,
  flaeche,
  hervorgehoben,
  onKlick,
}: {
  a: Annotation;
  zuPixel: (p: number[]) => { x: number; y: number };
  flaeche: { breite: number; hoehe: number };
  hervorgehoben: boolean;
  onKlick: () => void;
}) {
  const staerke = a.strichstaerke * (hervorgehoben ? 1.8 : 1);
  const gestrichelt = a.herkunft === 'KI_VORSCHLAG' ? [8, 4] : undefined;

  /*
   * Die Schwärzung wird hier deckend gezeichnet, damit sichtbar ist, was
   * sie verdecken WIRD. Verdeckt ist sie an dieser Stelle aber nicht: das
   * Bild darunter ist unverändert, und wer darf, sieht das Original.
   * Wirksam wird die Schwärzung erst in der Kundenfassung, die der Server
   * rechnet.
   */
  if (a.form === 'SCHWAERZUNG') {
    const p = zuPixel([a.geometrie.x, a.geometrie.y]);
    return (
      <>
        <Rect
          x={p.x}
          y={p.y}
          width={a.geometrie.breite * flaeche.breite}
          height={a.geometrie.hoehe * flaeche.hoehe}
          fill="#000000"
          stroke={hervorgehoben ? '#ffffff' : undefined}
          strokeWidth={hervorgehoben ? 2 : 0}
          dash={hervorgehoben ? [6, 4] : undefined}
          onClick={onKlick}
          onTap={onKlick}
        />
        <Nummer a={a} x={p.x} y={p.y} flaeche={flaeche} />
      </>
    );
  }

  if (a.form === 'RECHTECK' || a.form === 'TEXT') {
    const p = zuPixel([a.geometrie.x, a.geometrie.y]);
    return (
      <>
        <Rect
          x={p.x}
          y={p.y}
          width={a.geometrie.breite * flaeche.breite}
          height={a.geometrie.hoehe * flaeche.hoehe}
          stroke={a.farbe}
          strokeWidth={staerke}
          dash={gestrichelt}
          onClick={onKlick}
          onTap={onKlick}
        />
        {a.text && (
          <Text
            x={p.x + 4}
            y={p.y + 4}
            text={a.text}
            fontSize={Math.max(12, flaeche.breite / 45)}
            fill={a.farbe}
            listening={false}
          />
        )}
        <Nummer a={a} x={p.x} y={p.y} flaeche={flaeche} />
      </>
    );
  }

  if (a.form === 'ELLIPSE') {
    const mitte = zuPixel([
      a.geometrie.x + a.geometrie.breite / 2,
      a.geometrie.y + a.geometrie.hoehe / 2,
    ]);
    return (
      <>
        <Ellipse
          x={mitte.x}
          y={mitte.y}
          radiusX={(a.geometrie.breite * flaeche.breite) / 2}
          radiusY={(a.geometrie.hoehe * flaeche.hoehe) / 2}
          stroke={a.farbe}
          strokeWidth={staerke}
          dash={gestrichelt}
          onClick={onKlick}
          onTap={onKlick}
        />
        <Nummer a={a} x={mitte.x} y={mitte.y} flaeche={flaeche} />
      </>
    );
  }

  const von = zuPixel(a.geometrie.von);
  const nach = zuPixel(a.geometrie.nach);
  const Strecke = a.form === 'PFEIL' ? Arrow : Line;
  return (
    <>
      <Strecke
        points={[von.x, von.y, nach.x, nach.y]}
        fill={a.farbe}
        pointerLength={Math.max(8, staerke * 3)}
        pointerWidth={Math.max(8, staerke * 3)}
        stroke={a.farbe}
        strokeWidth={staerke}
        dash={gestrichelt}
        lineCap="round"
        onClick={onKlick}
        onTap={onKlick}
      />
      {a.mass_mm != null && (
        <Text
          x={(von.x + nach.x) / 2}
          y={(von.y + nach.y) / 2 - 18}
          text={
            `${a.merkmal ? `${a.merkmal}: ` : ''}${a.mass_mm} mm` +
            (a.bewertung === 'UEBERSCHRITTEN' ? ' ⚠' : '')
          }
          fontSize={Math.max(12, flaeche.breite / 45)}
          fontStyle="bold"
          fill={a.farbe}
          listening={false}
        />
      )}
      <Nummer a={a} x={von.x} y={von.y} flaeche={flaeche} />
    </>
  );
}

function Nummer({
  a,
  x,
  y,
  flaeche,
}: {
  a: Annotation;
  x: number;
  y: number;
  flaeche: { breite: number; hoehe: number };
}) {
  if (a.nummer == null) return null;
  return (
    <Text
      x={x - 6}
      y={y - Math.max(18, flaeche.breite / 40)}
      text={String(a.nummer)}
      fontSize={Math.max(14, flaeche.breite / 38)}
      fontStyle="bold"
      fill={a.farbe}
      listening={false}
    />
  );
}

function Entwurf({
  form,
  entwurf,
  farbe,
  staerke,
  zuPixel,
}: {
  form: Form;
  entwurf: { von: number[]; nach: number[] };
  farbe: string;
  staerke: number;
  zuPixel: (p: number[]) => { x: number; y: number };
}) {
  const von = zuPixel(entwurf.von);
  const nach = zuPixel(entwurf.nach);

  if (form === 'RECHTECK' || form === 'TEXT' || form === 'SCHWAERZUNG') {
    return (
      <Rect
        x={Math.min(von.x, nach.x)}
        y={Math.min(von.y, nach.y)}
        width={Math.abs(nach.x - von.x)}
        height={Math.abs(nach.y - von.y)}
        fill={form === 'SCHWAERZUNG' ? '#000000' : undefined}
        stroke={form === 'SCHWAERZUNG' ? '#ffffff' : farbe}
        strokeWidth={staerke}
        dash={[6, 4]}
        listening={false}
      />
    );
  }
  if (form === 'ELLIPSE') {
    return (
      <Ellipse
        x={(von.x + nach.x) / 2}
        y={(von.y + nach.y) / 2}
        radiusX={Math.abs(nach.x - von.x) / 2}
        radiusY={Math.abs(nach.y - von.y) / 2}
        stroke={farbe}
        strokeWidth={staerke}
        dash={[6, 4]}
        listening={false}
      />
    );
  }
  return (
    <Line
      points={[von.x, von.y, nach.x, nach.y]}
      stroke={farbe}
      strokeWidth={staerke}
      dash={[6, 4]}
      lineCap="round"
      listening={false}
    />
  );
}
